import { Component, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { AppComponent } from '../../app.component';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { PaginationHelper } from '../../shared/services/pagination.service';
import { RefundService } from '../../features/refund/services/refund.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { PermissionService } from '../../shared/services/permission.service';

@Component({
  selector: 'app-refund',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    AgGridAngular,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './refund.component.html',
  styleUrl: './refund.component.scss',
})
export class RefundComponent implements OnInit {
  public viewPortSize: boolean = false;
  public rowData: any[] | null = null;
  public loading: boolean = true;
  public error: any = null;
  private searchSubject = new Subject<string>();
  readonly dialog = inject(MatDialog);
  startDate: any;
  endDate: any;
  dateRangeForm: FormGroup;

  dateFilter = (date: Date | null): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Remove time component
    return date ? date <= today : false;
  };

  columnDefs: (
    | {
        headerName: string;
        field: string;
        flex: number;
        valueFormatter?: undefined;
        cellRenderer?: undefined;
        floatingFilter?: undefined;
        filter?: undefined;
      }
    | {
        headerName: string;
        field: string;
        flex: number;
        valueFormatter: (params: any) => string;
        cellRenderer?: undefined;
        floatingFilter?: undefined;
        filter?: undefined;
      }
    | {
        field: string;
        headerName: string;
        flex: number;
        cellRenderer: (params: any) => HTMLDivElement;
        floatingFilter: boolean;
        filter: boolean;
        valueFormatter?: undefined;
      }
  )[];
  page = 0;
  pageSize = 10;
  totalPages = 1;
  pageSizeOptions = [5, 10, 20, 50, 100];
  public paginationPageSizeSelector: number[] | boolean = [10, 25, 50];
    canEditRefund$ = this.permissionService.hasPermission(
        ModuleIds.Refund,
        'Edit'
      );
      canDeleteRefund$ = this.permissionService.hasPermission(
        ModuleIds.Refund,
        'Delete'
      );
      canCreateRefund$ = this.permissionService.hasPermission(
        ModuleIds.Refund,
        'Create'
      );
      canEditRefund:boolean = false;
      canDeleteRefund:boolean = false;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.setAGColumn();
  }

  setAGColumn() {
    this.columnDefs = [
      {
        headerName: 'Reference Number',
        field: 'referenceNumber',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Refund Amount',
        field: 'amount',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Refund Date',
        field: 'refundDate',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Customer Name',
        field: 'customerName',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Account Name',
        field: 'accountName',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Refund Status',
        field: 'refundStatus',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'action',
        headerName: 'Actions',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          const container = document.createElement('div');
          container.style.display = 'flex';
          container.style.alignItems = 'center';
          container.style.gap = '20px';
          console.log(params);

          const status = params.data?.refundStatus;
          const isEditable = status === 'Pending';

          if (isEditable) {
          const editIcon = document.createElement('img');
          editIcon.src = 'assets/images/icons/icon-edit.svg';
          editIcon.style.cursor = 'pointer';
          editIcon.setAttribute('data-action', 'edit');
          if(this.canEditRefund){
            container.appendChild(editIcon);
          }

          const deleteIcon = document.createElement('img');
          deleteIcon.src = 'assets/images/icons/icon-delete.svg';
          deleteIcon.style.cursor = 'pointer';
          deleteIcon.setAttribute('data-action', 'delete');
          if(this.canDeleteRefund){
          container.appendChild(deleteIcon);
          }
        }

          return container;
        },
        floatingFilter: false,
        filter: false,
      },
    ];
  }

  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public themeClass: string = 'ag-theme-quartz';

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private refundService: RefundService,
    private datePipe: DatePipe,
    private appComponent: AppComponent,
    private fb: FormBuilder,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.getAllRefunds();
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getAllRefunds(searchTerm);
      });

    this.checkViewportSize();

    this.dateRangeForm = this.fb.group({
      startDate: [''],
      endDate: [''],
      customerName: [''],
    });

    this.appComponent.toggleSidebar();
    this.canDeleteRefund$.subscribe(val => {
      this.canDeleteRefund = val;
    });
    this.canEditRefund$.subscribe(val => {
      this.canEditRefund = val;
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.pageSize = 10;
    this.page = 0;
    this.searchSubject.next(searchTerm);
  }

  nextPage() {
    if (this.page + 1 < this.totalPages) {
      this.page++;
      this.getAllRefunds('', this.pageSize);
    }
  }

  prevPage() {
    if (this.page > 0) {
      this.page--;
      this.getAllRefunds('', this.pageSize);
    }
  }

  onPageSizeChange(event: Event): void {
    const newSize = Number((event.target as HTMLSelectElement).value);
    console.log('New Page Size:', newSize);
    this.pageSize = newSize;
    this.page = 0;
    this.getAllRefunds('', this.pageSize);
  }

  onDateChange(event: any, type: string) {
    const formattedDate = this.datePipe.transform(event.value, 'yyyy-MM-dd');
    if (type === 'start') {
      this.startDate = formattedDate;
    } else if (type === 'end') {
      this.endDate = formattedDate;
    }
    if (this.startDate != null && this.endDate != null) {
      this.getAllRefunds();
    }
  }

  getAllRefunds(search: string = '', pageSize: number = 10) {
    this.loading = true;
    if (pageSize) {
      this.pageSize = pageSize;
    }
    const skip = this.page * this.pageSize;

    const input = {
      limit: this.pageSize,
      skip: skip,
      search: search,
      startDate: this.startDate,
      endDate: this.endDate,
    };

    this.refundService.getAllRefunds(input).subscribe({
      next: (data) => {
        this.totalPages = Math.ceil(
          (data?.total || data.refunds?.length) / this.pageSize
        );

        this.rowData = data?.refunds.map((refund: any) => {
          return {
            id: refund?.id,
            referenceNumber: refund?.referenceNumber || '--',
            refundStatus: refund?.refundStatus || '--',
            amount:
              refund?.amount != null
                ? `AED ${Number(refund.amount).toFixed(2)}`
                : '--',
            refundDate: refund?.refundDate
              ? new Date(refund.refundDate).toLocaleDateString('en-GB') // dd/mm/yyyy
              : '--',
            customerName:
              `${refund?.customerData?.firstName ?? ''} ${
                refund?.customerData?.lastName ?? ''
              }`.trim() || '--',
            accountName: refund?.accountData?.accountName || '',
          };
        });
        if (this.rowData) {
          this.paginationPageSizeSelector =
            PaginationHelper.getPaginationPageSizeOptions(this.rowData);
        } else {
          this.paginationPageSizeSelector = [];
        }
        this.loading = false;
      },
    });
  }

  openEdit(refundData: any) {
    const invoiceId = refundData?.id;
    if (invoiceId) {
      this.router.navigate([`/refund/refund-edit/${invoiceId}`]);
    }
  }

  onCancelRefund(refundData: any): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Confirm cancel',
        message: 'Are you sure you want to cancel this refund?',
        confirmText: 'Confirm',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.cancelRefund(refundData?.id);
      }
    });
  }

  private cancelRefund(refundId: number): void {
    this.refundService.removeRefund(refundId).subscribe({
      next: () => {
        this.toastr.success('Refund cancelled successfully.');
        this.getAllRefunds();
      },
      error: (error) => {
        this.toastr.error(
          error.message || 'Failed to cancel refund. Please try again.'
        );
      },
    });
  }

  onCellClicked(params: any) {
    if (
      params.column.colId === 'action' &&
      params.event.target.dataset.action
    ) {
      let action = params.event.target.dataset.action;

      if (action === 'edit') {
        this.openEdit(params?.data);
      } else if (action === 'delete') {
        this.onCancelRefund(params?.data);
      }
    } else {
      const id = params.data?.id;
      // this.router.navigate([`invoice/invoice-details/${invoiceId}`]);
      this.router.navigate([`refund/refund-details`], {
        queryParams: { refundId: id },
      });
    }
  }

  onClear() {
    this.startDate = null;
    this.endDate = null;
    this.dateRangeForm.reset();
    this.getAllRefunds();
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  isDropdownOpen = false;

  toggleDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
  }
}
